import { z } from "zod";

const optionalTrimmed = z
  .string()
  .trim()
  .max(200)
  .optional()
  .nullable()
  .transform((v) => (v === "" ? null : (v ?? null)));

/** descrizioni lunghe: ATECO, NACE, URL di origine… */
const optionalLong = z
  .string()
  .trim()
  .max(500)
  .optional()
  .nullable()
  .transform((v) => (v === "" ? null : (v ?? null)));

const optionalDay = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Data non valida (attesa AAAA-MM-GG)")
  .optional()
  .nullable()
  .transform((v) => (v === "" ? null : (v ?? null)));

const optionalCount = z.coerce.number().int().min(0).max(100_000_000).optional().nullable();
const optionalAmount = z.coerce.number().int().optional().nullable();
const optionalFlag = z.boolean().optional().nullable();

export const sortDirSchema = z.enum(["asc", "desc"]);
export type SortDir = z.infer<typeof sortDirSchema>;

export const listQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(300).default(25),
  // ordinamento server-side: chiave colonna (whitelist per route) + direzione
  sort: z.string().trim().max(50).optional(),
  dir: sortDirSchema.optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

// ── aziende ─────────────────────────────────────────────────────────

/**
 * Dati di registro imprese: popolati dall'import (formato Atoka e simili)
 * ma modificabili anche a mano dalla scheda azienda.
 */
export const companyRegistryShape = {
  fiscalCode: optionalTrimmed,
  reaNumber: optionalTrimmed,
  reaProvince: optionalTrimmed,
  legalForm: optionalTrimmed,
  foundedOn: optionalDay,
  atecoCode: optionalTrimmed,
  atecoDescription: optionalLong,
  naceCode: optionalTrimmed,
  naceDescription: optionalLong,
  region: optionalTrimmed,
  geoArea: optionalTrimmed,
  employees: optionalCount,
  /** ricavi in centesimi */
  revenueCents: optionalAmount,
  balanceDate: optionalDay,
  localUnits: optionalCount,
  isStartup: optionalFlag,
  isOperational: optionalFlag,
  isListed: optionalFlag,
  cms: optionalTrimmed,
  ecommerce: optionalTrimmed,
  source: optionalTrimmed,
  sourceUrl: optionalLong,
} as const;

export const companyRegistrySchema = z.object(companyRegistryShape);
export type CompanyRegistryInput = z.infer<typeof companyRegistrySchema>;

export const companyInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  vatNumber: optionalTrimmed,
  website: optionalTrimmed,
  industry: optionalTrimmed,
  email: optionalTrimmed,
  phone: optionalTrimmed,
  address: optionalTrimmed,
  city: optionalTrimmed,
  zip: optionalTrimmed,
  province: optionalTrimmed,
  country: z.string().trim().length(2).default("IT"),
  ownerId: z.string().uuid().optional().nullable(),
  ...companyRegistryShape,
});
export type CompanyInput = z.infer<typeof companyInputSchema>;

// ── contatti ────────────────────────────────────────────────────────
export const contactInputSchema = z.object({
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().max(120).default(""),
  email: optionalTrimmed,
  phone: optionalTrimmed,
  mobile: optionalTrimmed,
  jobTitle: optionalTrimmed,
  companyId: z.string().uuid().optional().nullable(),
  address: optionalTrimmed,
  city: optionalTrimmed,
  zip: optionalTrimmed,
  province: optionalTrimmed,
  country: z.string().trim().length(2).default("IT"),
  marketingConsent: z.boolean().default(false),
  statusId: z.string().uuid().optional().nullable(),
  ownerId: z.string().uuid().optional().nullable(),
});
export type ContactInput = z.infer<typeof contactInputSchema>;

// ── stati contatto ──────────────────────────────────────────────────
/** Token colore ammessi per gli stati contatto (mappati a classi UI). */
export const CONTACT_STATUS_COLORS = [
  "slate",
  "blue",
  "cyan",
  "emerald",
  "amber",
  "orange",
  "red",
  "violet",
  "pink",
] as const;
export type ContactStatusColor = (typeof CONTACT_STATUS_COLORS)[number];

export const contactStatusInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  color: z.enum(CONTACT_STATUS_COLORS).default("slate"),
  position: z.number().int().min(0).optional(),
});
export type ContactStatusInput = z.infer<typeof contactStatusInputSchema>;

/** Stati contatto creati di default per ogni nuovo tenant. */
export const DEFAULT_CONTACT_STATUSES = [
  { name: "Lead", color: "blue" },
  { name: "Prospect", color: "amber" },
  { name: "Cliente", color: "emerald" },
  { name: "Non interessato", color: "slate" },
] as const;

// ── liste di contatti ───────────────────────────────────────────────
export const contactListInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z
    .string()
    .trim()
    .max(500)
    .optional()
    .nullable()
    .transform((v) => (v === "" ? null : (v ?? null))),
});
export type ContactListInput = z.infer<typeof contactListInputSchema>;

/** Aggiunta membri in blocco (dalla pagina Contatti o dal dettaglio lista). */
export const contactListMembersSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1).max(1000),
});
export type ContactListMembersInput = z.infer<typeof contactListMembersSchema>;

// ── opportunità ─────────────────────────────────────────────────────
export const dealInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  pipelineId: z.string().uuid(),
  stageId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional().nullable(),
  contactId: z.string().uuid().optional().nullable(),
  valueCents: z.coerce.number().int().min(0).default(0),
  currency: z.string().length(3).default("EUR"),
  expectedCloseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  ownerId: z.string().uuid().optional().nullable(),
  notes: z.string().trim().max(10000).optional().nullable(),
});
export type DealInput = z.infer<typeof dealInputSchema>;

export const dealUpdateSchema = dealInputSchema.partial().extend({
  lostReason: z.string().trim().max(500).optional().nullable(),
});

export const dealMoveSchema = z.object({
  stageId: z.string().uuid(),
  position: z.number().optional(),
  lostReason: z.string().trim().max(500).optional().nullable(),
});

// ── attività ────────────────────────────────────────────────────────
export const ACTIVITY_TYPES = ["task", "call", "meeting", "email", "deadline"] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const activityInputSchema = z.object({
  type: z.enum(ACTIVITY_TYPES).default("task"),
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().max(5000).optional().nullable(),
  dueAt: z.string().datetime({ offset: true }).optional().nullable(),
  assignedTo: z.string().uuid().optional().nullable(),
  contactId: z.string().uuid().optional().nullable(),
  companyId: z.string().uuid().optional().nullable(),
  dealId: z.string().uuid().optional().nullable(),
});
export type ActivityInput = z.infer<typeof activityInputSchema>;

export const activityUpdateSchema = activityInputSchema.partial().extend({
  done: z.boolean().optional(),
});

// ── note ────────────────────────────────────────────────────────────
export const NOTE_ENTITIES = ["contact", "company", "deal", "project"] as const;

export const noteInputSchema = z.object({
  entityType: z.enum(NOTE_ENTITIES),
  entityId: z.string().uuid(),
  body: z.string().trim().min(1).max(10000),
});

// ── pipeline ────────────────────────────────────────────────────────
export const pipelineInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
});

export const stageInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  position: z.number().int().min(0).optional(),
  probability: z.number().int().min(0).max(100).optional(),
});

/** Stage della pipeline di default creata per ogni nuovo tenant. */
export const DEFAULT_PIPELINE_STAGES = [
  { name: "Nuovo", probability: 10, isWon: false, isLost: false },
  { name: "Contattato", probability: 25, isWon: false, isLost: false },
  { name: "Proposta inviata", probability: 50, isWon: false, isLost: false },
  { name: "Negoziazione", probability: 75, isWon: false, isLost: false },
  { name: "Vinto", probability: 100, isWon: true, isLost: false },
  { name: "Perso", probability: 0, isWon: false, isLost: true },
] as const;

// ── potenziale dell'azienda (analisi prospect) ──────────────────────
export const COMPANY_RATINGS = ["A", "B", "C"] as const;
export const RELATIONSHIPS = ["prospect", "customer", "inactive"] as const;
export const RELATIONSHIP_LABEL: Record<(typeof RELATIONSHIPS)[number], string> = { prospect: "Prospect", customer: "Cliente", inactive: "Non attivo" };
export const companyPotentialSchema = z.object({
  companyId: z.string().uuid(),
  potentialCents: z.number().int().min(0).max(100_000_000_000),
  rating: z.enum(COMPANY_RATINGS).nullable(),
  relationship: z.enum(RELATIONSHIPS).default("prospect"),
  note: z.string().trim().max(1000).nullable().default(null),
});
